// V2.5 client-side analytics emitter — extended in V4.1 with a full funnel
// catalog so the CrazyGames Basic Launch dashboard can be debugged at the
// event-level instead of only the aggregate-metric level.
//
// Two layers:
//   1. `emit({url, event})` — the stateless one-shot beacon used by the
//      original V2.5 match_started call site. Kept for back-compat so the
//      existing analytics.test.ts keeps passing.
//   2. `AnalyticsEmitter` (singleton `analytics`) — context-aware queue
//      that batches events, flushes on size / interval / visibilitychange /
//      pagehide / beforeunload, and respects the analyticsEnabled opt-out.
//      Every other call site in the app uses this.
//
// Privacy boundary (docs/00_README.md "Privacy"):
//   - No IP, fingerprint, session-id, profile-id, or user-id on the wire.
//   - The server allowlist (`apps/server/src/analytics/Analytics.ts`
//     `validateEvent`) rejects any post that includes a banned field.
//   - Without a session id we cannot perfectly stitch events into sessions,
//     but every event is self-contained (elapsedMs / atSec / matchSec
//     measured from a local anchor) so funnel-style analysis still works.
//
// Bot-fill double-count guard:
//   - The server is authoritative for `match_completed` / `outcome_recorded`.
//   - The client emits `match_started` (and the rest of the funnel events).

// ---------------------------------------------------------------------------
// Event catalog
// ---------------------------------------------------------------------------

// Section A — Session & boot funnel.
export interface SessionStartedEvent {
  kind: 'session_started';
  device: 'desktop' | 'mobile' | 'tablet';
  touch: boolean;
  screenW: number;
  screenH: number;
}
export interface SdkReadyEvent {
  kind: 'sdk_ready';
  elapsedMs: number;
}
export interface SdkLoadingStopEvent {
  kind: 'sdk_loading_stop';
  elapsedMs: number;
}
export interface AtlasGeneratedEvent {
  kind: 'atlas_generated';
  elapsedMs: number;
}
export interface MainMenuVisibleEvent {
  kind: 'main_menu_visible';
  elapsedMs: number;
}
export interface AudioUnlockedEvent {
  kind: 'audio_unlocked';
  elapsedMs: number;
}
export interface VisibilityChangedEvent {
  kind: 'visibility_changed';
  hidden: boolean;
  hiddenForMs?: number;
}
export interface SessionEndedEvent {
  kind: 'session_ended';
  totalMs: number;
  lastScene: string;
}

// Section B — Main menu interactions.
export interface MenuShownEvent {
  kind: 'menu_shown';
  firstSession: boolean;
}
export interface MenuPlayNowClickedEvent {
  kind: 'menu_play_now_clicked';
  msSinceShown: number;
}
export interface MenuOnlineClickedEvent {
  kind: 'menu_online_clicked';
  msSinceShown: number;
}
export interface MenuInviteClickedEvent {
  kind: 'menu_invite_clicked';
  msSinceShown: number;
}
export interface MenuSettingsOpenedEvent {
  kind: 'menu_settings_opened';
}
export interface MenuProfileOpenedEvent {
  kind: 'menu_profile_opened';
}
export interface MenuDifficultyChangedEvent {
  kind: 'menu_difficulty_changed';
  from: string;
  to: string;
  msSinceShown: number;
}
export interface MenuMapChangedEvent {
  kind: 'menu_map_changed';
  from: string;
  to: string;
  msSinceShown: number;
}
export interface MenuCustomizeExpandedEvent {
  kind: 'menu_customize_expanded';
  msSinceShown: number;
}

// Section C — Intro overlay + tutorial.
export interface IntroShownEvent {
  kind: 'intro_shown';
}
export interface IntroDismissedEvent {
  kind: 'intro_dismissed';
  elapsedMs: number;
  method: 'cta' | 'click' | 'key' | 'auto';
}
export interface IntroAbandonedEvent {
  kind: 'intro_abandoned';
  elapsedMs: number;
}
export interface TutorialStepShownEvent {
  kind: 'tutorial_step_shown';
  stepId: string;
  stepIdx: number;
  totalSteps: number;
}
export interface TutorialStepCompletedEvent {
  kind: 'tutorial_step_completed';
  stepId: string;
  elapsedMs: number;
}
export interface TutorialSkippedEvent {
  kind: 'tutorial_skipped';
  atStepId: string;
  elapsedMs: number;
}
export interface TutorialCompletedEvent {
  kind: 'tutorial_completed';
  totalElapsedMs: number;
}

// Section D — In-match milestones.
export interface MatchStartedEvent {
  kind: 'match_started';
  roomType: 'signal_clash' | 'signal_clash_quick' | 'local';
  // Extended in V4.1 with enough match-shape context that an aggregator
  // can cross-reference completion outcomes by map / difficulty without
  // joining tables. Existing test asserts the minimal shape; all new
  // fields are optional so back-compat holds.
  mapId?: string;
  difficulty?: string;
  isFirstMatch?: boolean;
  totalMatchesBefore?: number;
  doctrineId?: string | null;
}
export interface MatchCheckpointEvent {
  kind: 'match_checkpoint';
  atSec: number;
  viewerRelays: number;
  viewerSquads: number;
  viewerSignal: number;
}
export interface FirstSquadBuiltEvent {
  kind: 'first_squad_built';
  elapsedMs: number;
  archetype: string;
}
export interface FirstRelayCapturedEvent {
  kind: 'first_relay_captured';
  elapsedMs: number;
}
export interface FirstSquadLostEvent {
  kind: 'first_squad_lost';
  elapsedMs: number;
}
export interface FirstKillEvent {
  kind: 'first_kill';
  elapsedMs: number;
  archetype: string;
}
export interface FirstAbilityUsedEvent {
  kind: 'first_ability_used';
  elapsedMs: number;
  abilityId: string;
}
export interface FirstHubBuiltEvent {
  kind: 'first_hub_built';
  elapsedMs: number;
}
export interface FirstDoctrinePickedEvent {
  kind: 'first_doctrine_picked';
  elapsedMs: number;
  doctrineId: string;
}
export interface MatchLeftEarlyEvent {
  kind: 'match_left_early';
  atSec: number;
  reason: 'menu_btn' | 'tab_close' | 'visibility_long' | 'forfeit';
}
export interface MatchCompletedEvent {
  kind: 'match_completed';
  outcomeKind: 'core_destroyed' | 'dominance' | 'overtime';
  matchSeconds: number;
  // V2.5 fields.
  botFilled?: boolean;
  // V4.1 extended fields.
  viewerKills?: number;
  enemyKills?: number;
  peakStreak?: number;
  doctrineId?: string;
  mapId?: string;
  difficulty?: string;
  won?: boolean;
}
export interface OutcomeRecordedEvent {
  kind: 'outcome_recorded';
  outcomeKind: 'core_destroyed' | 'dominance' | 'overtime';
}

// Section E — End-of-match interactions.
export interface EomShownEvent {
  kind: 'eom_shown';
  outcomeKind: 'core_destroyed' | 'dominance' | 'overtime';
  won: boolean;
}
export interface EomRematchClickedEvent {
  kind: 'eom_rematch_clicked';
  msShown: number;
  won: boolean;
}
export interface EomMenuClickedEvent {
  kind: 'eom_menu_clicked';
  msShown: number;
  won: boolean;
}
export interface EomAbandonedEvent {
  kind: 'eom_abandoned';
  msShown: number;
  won: boolean;
}

// Section F — Errors.
export interface ClientErrorEvent {
  kind: 'client_error';
  message: string;
  source: 'window' | 'unhandled_rejection' | 'phaser' | 'boot' | 'sdk';
  stack?: string;
  file?: string;
  line?: number;
}
export interface WebglContextLostEvent {
  kind: 'webgl_context_lost';
}
export interface AudioContextFailedEvent {
  kind: 'audio_context_failed';
  reason: string;
}
export interface NetworkDisconnectEvent {
  kind: 'network_disconnect';
  atSec: number;
  reconnected: boolean;
  reconnectMs?: number;
}
export interface AssetLoadFailedEvent {
  kind: 'asset_load_failed';
  url: string;
  assetType: string;
}
// V1.8 — cloud-save hydrate diagnostic. Without this event the dashboard
// can't distinguish "returning player with broken cloud sync" from "new
// player". A high failure rate here means our retention KPI (D1/D7) is
// being measured wrong, not just our retention experience being bad.
export interface CloudHydrateStatusEvent {
  kind: 'cloud_hydrate_status';
  outcome: 'success' | 'sdk_unavailable' | 'ready_timeout' | 'fetch_timeout' | 'error';
  keysAttempted: number;
  keysHydrated: number;
  durationMs: number;
  errorMessage?: string;
}

// Section G — Performance.
export interface PerfFrameDropEvent {
  kind: 'perf_frame_drop_sustained';
  atSec: number;
  fps: number;
  durationMs: number;
}
export interface PerfSnapLagEvent {
  kind: 'perf_snap_lag';
  atSec: number;
  snapDeltaMs: number;
}

// Section H — Meta-progression.
export interface UnlockReachedEvent {
  kind: 'unlock_reached';
  tierId: string;
  totalMatches: number;
}
export interface DailyChallengeProgressEvent {
  kind: 'daily_challenge_progress';
  challengeId: string;
  before: number;
  after: number;
}
export interface DailyChallengeCompletedEvent {
  kind: 'daily_challenge_completed';
  challengeId: string;
}
export interface ProfilePanelViewedEvent {
  kind: 'profile_panel_viewed';
}
// V1.9 — EOM grading + XP. Tier matches MatchMedals.MedalTier.
export interface EomMedalShownEvent {
  kind: 'eom_medal_shown';
  tier: 'gold' | 'silver' | 'bronze';
  won: boolean;
}
export interface EomXpAwardedEvent {
  kind: 'eom_xp_awarded';
  delta: number;
  level: number;
  leveledUp: boolean;
}
export interface LoginStreakEvent {
  kind: 'login_streak';
  streakDays: number;
  extended: boolean;
}
// V1.9 — boot watchdog. Fires once if the main menu doesn't surface
// within the boot deadline; treats "stuck loading" as crash-shaped per
// CrazyGamesTricks §10.6. `expectedPhase` names the bucket we should
// have reached by the deadline so the dashboard can attribute the
// failure to a specific stage.
export interface StuckLoadingEvent {
  kind: 'stuck_loading';
  elapsedMs: number;
  expectedPhase: string;
}

export type AnalyticsEvent =
  // A — session/boot
  | SessionStartedEvent
  | SdkReadyEvent
  | SdkLoadingStopEvent
  | AtlasGeneratedEvent
  | MainMenuVisibleEvent
  | AudioUnlockedEvent
  | VisibilityChangedEvent
  | SessionEndedEvent
  // B — menu
  | MenuShownEvent
  | MenuPlayNowClickedEvent
  | MenuOnlineClickedEvent
  | MenuInviteClickedEvent
  | MenuSettingsOpenedEvent
  | MenuProfileOpenedEvent
  | MenuDifficultyChangedEvent
  | MenuMapChangedEvent
  | MenuCustomizeExpandedEvent
  // C — intro + tutorial
  | IntroShownEvent
  | IntroDismissedEvent
  | IntroAbandonedEvent
  | TutorialStepShownEvent
  | TutorialStepCompletedEvent
  | TutorialSkippedEvent
  | TutorialCompletedEvent
  // D — in-match
  | MatchStartedEvent
  | MatchCheckpointEvent
  | FirstSquadBuiltEvent
  | FirstRelayCapturedEvent
  | FirstSquadLostEvent
  | FirstKillEvent
  | FirstAbilityUsedEvent
  | FirstHubBuiltEvent
  | FirstDoctrinePickedEvent
  | MatchLeftEarlyEvent
  | MatchCompletedEvent
  | OutcomeRecordedEvent
  // E — end of match
  | EomShownEvent
  | EomRematchClickedEvent
  | EomMenuClickedEvent
  | EomAbandonedEvent
  // F — errors
  | ClientErrorEvent
  | WebglContextLostEvent
  | AudioContextFailedEvent
  | NetworkDisconnectEvent
  | AssetLoadFailedEvent
  | CloudHydrateStatusEvent
  // G — performance
  | PerfFrameDropEvent
  | PerfSnapLagEvent
  // H — meta
  | UnlockReachedEvent
  | DailyChallengeProgressEvent
  | DailyChallengeCompletedEvent
  | ProfilePanelViewedEvent
  | EomMedalShownEvent
  | EomXpAwardedEvent
  | LoginStreakEvent
  | StuckLoadingEvent;

// ---------------------------------------------------------------------------
// Boot anchor — used to compute elapsedMs for early-boot events.
// ---------------------------------------------------------------------------

export const BOOT_TS_MS: number =
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

export function bootElapsedMs(): number {
  const now =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
  return Math.round(now - BOOT_TS_MS);
}

// ---------------------------------------------------------------------------
// Back-compat one-shot beacon API (used by the existing analytics test).
// ---------------------------------------------------------------------------

export interface EmitArgs {
  url: string | null | undefined;
  event: AnalyticsEvent;
}

// Returns true if the beacon was queued, false otherwise. Best-effort:
// false from sendBeacon (CORS / quota / disabled) is non-fatal.
export function emit(args: EmitArgs): boolean {
  const { url, event } = args;
  if (typeof url !== 'string' || url === '') return false;
  return sendOne(url, event);
}

function sendOne(url: string, event: AnalyticsEvent): boolean {
  if (typeof navigator === 'undefined') return false;
  const beacon = navigator.sendBeacon as
    | ((u: string | URL, data?: BodyInit | null) => boolean)
    | undefined;
  if (typeof beacon !== 'function') return false;
  try {
    const body =
      typeof Blob === 'function'
        ? new Blob([JSON.stringify(event)], { type: 'application/json' })
        : JSON.stringify(event);
    return beacon.call(navigator, url, body as BodyInit);
  } catch {
    return false;
  }
}

function sendBatch(url: string, batch: AnalyticsEvent[]): boolean {
  if (typeof navigator === 'undefined') return false;
  const beacon = navigator.sendBeacon as
    | ((u: string | URL, data?: BodyInit | null) => boolean)
    | undefined;
  if (typeof beacon !== 'function') return false;
  try {
    const body =
      typeof Blob === 'function'
        ? new Blob([JSON.stringify(batch)], { type: 'application/json' })
        : JSON.stringify(batch);
    return beacon.call(navigator, url, body as BodyInit);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// AnalyticsEmitter — batched queue with visibility + unload flush hooks.
// ---------------------------------------------------------------------------

const DEFAULT_FLUSH_SIZE = 10;
const DEFAULT_FLUSH_INTERVAL_MS = 5_000;
// Hard cap on the in-memory queue so an early-boot URL-less period (or a
// silently failing server) can't grow the queue unboundedly. Excess events
// are dropped silently — analytics is best-effort.
const MAX_QUEUE = 256;

export interface AnalyticsEmitterOptions {
  url?: string | null;
  enabledFn?: () => boolean;
  flushSize?: number;
  flushIntervalMs?: number;
}

export class AnalyticsEmitter {
  private url: string | null = null;
  private enabledFn: () => boolean = () => true;
  private readonly flushSize: number;
  private readonly flushIntervalMs: number;
  private queue: AnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private installed = false;
  private lastHiddenAt: number | null = null;

  constructor(opts: AnalyticsEmitterOptions = {}) {
    this.url = opts.url ?? null;
    if (opts.enabledFn !== undefined) this.enabledFn = opts.enabledFn;
    this.flushSize = opts.flushSize ?? DEFAULT_FLUSH_SIZE;
    this.flushIntervalMs = opts.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  }

  setUrl(url: string | null): void {
    this.url = url;
    // Drain anything queued before the URL was known.
    if (this.url !== null && this.queue.length > 0) this.flush();
  }

  setEnabledCheck(fn: () => boolean): void {
    this.enabledFn = fn;
  }

  getUrl(): string | null {
    return this.url;
  }

  // Install document-level hooks (visibility / pagehide / beforeunload)
  // so queued events flush before the page goes away. Idempotent — safe
  // to call multiple times during boot races.
  install(): void {
    if (this.installed) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    this.installed = true;
    document.addEventListener('visibilitychange', () => {
      const hidden = document.visibilityState === 'hidden';
      if (hidden) {
        this.lastHiddenAt = Date.now();
        this.emit({ kind: 'visibility_changed', hidden: true });
        this.flush();
      } else {
        const hiddenForMs =
          this.lastHiddenAt !== null ? Date.now() - this.lastHiddenAt : 0;
        this.lastHiddenAt = null;
        this.emit({ kind: 'visibility_changed', hidden: false, hiddenForMs });
      }
    });
    // pagehide is the iOS-Safari-friendly version of beforeunload — fires
    // reliably on tab close / back-navigation / bfcache eviction. We listen
    // for both for cross-browser coverage; double-flushes are harmless
    // because flush() empties and short-circuits on empty queue.
    window.addEventListener('pagehide', () => {
      this.flush();
    });
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  // Queue an event. Honors the opt-out predicate; drops silently when the
  // queue is at the hard cap.
  emit(event: AnalyticsEvent): void {
    if (!this.enabledFn()) return;
    if (this.queue.length >= MAX_QUEUE) return;
    this.queue.push(event);
    if (this.queue.length >= this.flushSize) {
      this.flush();
      return;
    }
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    if (typeof setTimeout === 'undefined') return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, this.flushIntervalMs);
  }

  // Flush whatever is queued. No-op when queue is empty or URL isn't set yet
  // (events held until setUrl is called). The send is best-effort; failures
  // drop the batch on the floor rather than retrying.
  flush(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) return;
    if (this.url === null) return;
    const batch = this.queue;
    this.queue = [];
    sendBatch(this.url, batch);
  }

  // Test-only — peek at the queued events without flushing.
  _peekQueueForTests(): readonly AnalyticsEvent[] {
    return this.queue;
  }

  // Test-only — reset queue/timer state.
  _resetForTests(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.queue = [];
    this.lastHiddenAt = null;
    this.installed = false;
  }
}

// Module-level singleton. main.ts wires its URL + opt-out check and calls
// install() once during boot. Every other call site imports `analytics`
// and calls `analytics.emit({kind: ..., ...})`.
export const analytics = new AnalyticsEmitter();

// ---------------------------------------------------------------------------
// URL derivation
// ---------------------------------------------------------------------------

// Helper: derive the HTTP analytics URL from the WebSocket endpoint
// the client is configured to talk to. The two share the same host
// and port; we just swap the protocol.
export function analyticsUrlFromWsEndpoint(wsEndpoint: string): string | null {
  try {
    const url = new URL(wsEndpoint);
    const httpProto = url.protocol === 'wss:' ? 'https:' : 'http:';
    return `${httpProto}//${url.host}/analytics/event`;
  } catch {
    return null;
  }
}

// Detect a coarse device class from the current viewport + UA. Used as the
// `device` field on `session_started`. The split is intentionally coarse —
// the CrazyGames dashboard already separates desktop vs mobile/tablet, and
// we want bucket counts large enough to be statistically meaningful inside
// a 200-play day.
export function classifyDevice(): 'desktop' | 'mobile' | 'tablet' {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent ?? '';
  const isMobileUa = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  if (!isMobileUa) return 'desktop';
  // Heuristic: > 800 px shortest side is a tablet, otherwise a phone.
  const short = Math.min(window.innerWidth, window.innerHeight);
  return short > 800 ? 'tablet' : 'mobile';
}
